/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
// thunks
import { 
    fetchTotalCommentsReport, 
    fetchTotalCommentingViewsReport,
    fetchCommentingUsersReport, 
    fetchTotalCommentingReactionsReport, 
    fetchTopArticlesReport,
    fetchCommentingBeelineReport,
    fetchEmailsSentReport,
    fetchImportLists,
    fetchCommentingConfig,
    fetchCommentingUnitCode,
    getMigrationStatus,
    getUpdatedCodes,
    getAddOns,
    updateAddOnsSetting,
    createAddOnsSetting,
    updateAvatarPack,
    updateCommentsTree,
    updateColorSchema,
    updateCommunity,
    updateModeration,
    updateGeneral,
    fetchCommentingRequestStatus,
    approveCommentingAccessRequest,
    getCommentsCounterCode,
    fetchAvgInViewReport
} from '../thunks/commenting';
import { fetchProductDataLastUpdated } from '../thunks/product';


const initialCommentingUnitCodes = {
    commentingUnitCode: {
        headerCode: '',
        bodyCode: ''
    },
    communityCode: { code: '' },
    commentsCounterCode: { bodyCode: ''}
}

const initialCommentingAddOns = {
  community: {
      uuid: '',
      isCommunityEnabled: false,
      code: '',
  },  
  commentsCounter: {
    uuid: '',
    headerCode: '',
    bodyCode: '',
    isCommentsCounterEnabled: false,
    zeroCommentsLabel: '',
    singleCommentLabel: '',
    multipleCommentsLabel: ''
  }
}

const detailsDefaultData = { daily: {}, weekly: {}, monthly: {}, quarterly: {}}

/* ========== ~~~~~~~~~~ COMMENTING SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialState = {
    lastUpdated: null,
    totalComments: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    totalCommentingViews: {
      overview: null,
      details: detailsDefaultData,
      status: 'initial',
      isDataReceived: false
    },
    commentingUsers: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    totalCommentingReactions: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    topArticlesByEngagement: {
        data: [],
        status: 'initial',
        isDataReceived: false
    },
    commentingBeeline: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    emailsSent: {
        overview: null,
        details: detailsDefaultData,
        status: 'initial',
        isDataReceived: false
    },
    avgInView: {
      overview: null,
      details: detailsDefaultData,
      status: 'initial',
      isDataReceived: false
    },
    importLists: {
        status: 'initial',
        data: []
    },
    commentingConfig: {
        status: 'initial',
        isUpdating: {
          general: false,
          moderation: false,
          commentsTree: false,
          avatarPack: false,
          community: false,
          colorSchema: false
        },
        data: null
    },
    commentingUnitCode: {
        status: 'initial',
        bodyCode: '',
        headerCode: ''
    },
    commentingAdvanced: {
        isError: false,
        isFetching: false,
        migrationStatus: null,
        codes: initialCommentingUnitCodes
    },
    commentingAddOns: {
        isFetching: false,
        isUpdating: false,
        isUpdated: true,
        isCreating: false,
        isCreated: false,
        addOns: initialCommentingAddOns
    },
    commentingAccessRequest: {
      requestStatus: null,
      userEmail: null,
      isFetching: false,
      isApproving: false
    }
};


export const commentingSlice = createSlice({
    name: 'commenting',
    initialState,
    reducers: {

    },
    extraReducers: builder => {
        builder
            // fetch product data last updated timestamp
            .addCase(fetchProductDataLastUpdated.pending, (state, action) => {
                state.lastUpdated = null;
            })
            .addCase(fetchProductDataLastUpdated.fulfilled, (state, action) => {
                const { last_updated } = action.payload
                state.lastUpdated = last_updated;
            })
            .addCase(fetchProductDataLastUpdated.rejected, (state, action) => {
                state.lastUpdated = null;
            })

            // fetch Total Comments Report
            .addCase(fetchTotalCommentsReport.pending, (state, action) => {
                state.totalComments.status = 'pending'
                state.totalComments.details = detailsDefaultData
            })
            .addCase(fetchTotalCommentsReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.totalComments.overview = Overview || state.totalComments.overview
                if(Daily) state.totalComments.details.daily = Daily;
                if(Weekly) state.totalComments.details.weekly = Weekly;
                if(Monthly) state.totalComments.details.monthly = Monthly;
                if(Quarterly) state.totalComments.details.quarterly = Quarterly;
                state.totalComments.status = 'fulfilled'
                state.totalComments.isDataReceived = true
            })
            .addCase(fetchTotalCommentsReport.rejected, (state, action) => {
                state.totalComments.status = 'rejected'
            })

            // fetch Total Commenting Views Report
            .addCase(fetchTotalCommentingViewsReport.pending, (state, action) => {
                state.totalCommentingViews.status = 'pending'
                state.totalCommentingViews.details = detailsDefaultData
            })
            .addCase(fetchTotalCommentingViewsReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.totalCommentingViews.overview = Overview || state.totalCommentingViews.overview
                if(Daily) state.totalCommentingViews.details.daily = Daily;
                if(Weekly) state.totalCommentingViews.details.weekly = Weekly;
                if(Monthly) state.totalCommentingViews.details.monthly = Monthly;
                if(Quarterly) state.totalCommentingViews.details.quarterly = Quarterly;
                state.totalCommentingViews.status = 'fulfilled'
                state.totalCommentingViews.isDataReceived = true
            })
            .addCase(fetchTotalCommentingViewsReport.rejected, (state, action) => {
                state.totalCommentingViews.status = 'rejected'
            })

            // fetch Commenting Users Report
            .addCase(fetchCommentingUsersReport.pending, (state, action) => {
                state.commentingUsers.status = 'pending'
                state.commentingUsers.details = detailsDefaultData
            })
            .addCase(fetchCommentingUsersReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.commentingUsers.overview = Overview || state.commentingUsers.overview
                if(Daily) state.commentingUsers.details.daily = Daily;
                if(Weekly) state.commentingUsers.details.weekly = Weekly;
                if(Monthly) state.commentingUsers.details.monthly = Monthly;
                if(Quarterly) state.commentingUsers.details.quarterly = Quarterly;
                state.commentingUsers.status = 'fulfilled'
                state.commentingUsers.isDataReceived = true
            })
            .addCase(fetchCommentingUsersReport.rejected, (state, action) => {
                state.commentingUsers.status = 'rejected'
            })

            // fetch Total Commenting Reactions Report
            .addCase(fetchTotalCommentingReactionsReport.pending, (state, action) => {
                state.totalCommentingReactions.status = 'pending'
                state.totalCommentingReactions.details = detailsDefaultData
            })
            .addCase(fetchTotalCommentingReactionsReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.totalCommentingReactions.overview = Overview || state.totalCommentingReactions.overview
                if(Daily) state.totalCommentingReactions.details.daily = Daily;
                if(Weekly) state.totalCommentingReactions.details.weekly = Weekly;
                if(Monthly) state.totalCommentingReactions.details.monthly = Monthly;
                if(Quarterly) state.totalCommentingReactions.details.quarterly = Quarterly;
                state.totalCommentingReactions.status = 'fulfilled'
                state.totalCommentingReactions.isDataReceived = true
            })
            .addCase(fetchTotalCommentingReactionsReport.rejected, (state, action) => {
                state.totalCommentingReactions.status = 'rejected'
            })

            // fetch Top Articles Report
            .addCase(fetchTopArticlesReport.pending, (state, action) => {
                state.topArticlesByEngagement.status = 'pending'
            })
            .addCase(fetchTopArticlesReport.fulfilled, (state, action) => {
                state.topArticlesByEngagement.data= action.payload
                state.topArticlesByEngagement.status = 'fulfilled'
                state.topArticlesByEngagement.isDataReceived = true
            })
            .addCase(fetchTopArticlesReport.rejected, (state, action) => {
                state.topArticlesByEngagement.status = 'rejected'
            })

            // fetch Commenting Beeline Report
            .addCase(fetchCommentingBeelineReport.pending, (state, action) => {
                state.commentingBeeline.status = 'pending'
                state.commentingBeeline.details = detailsDefaultData
            })
            .addCase(fetchCommentingBeelineReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.commentingBeeline.overview = Overview || state.commentingBeeline.overview
                if(Daily) state.commentingBeeline.details.daily = Daily;
                if(Weekly) state.commentingBeeline.details.weekly = Weekly;
                if(Monthly) state.commentingBeeline.details.monthly = Monthly;
                if(Quarterly) state.commentingBeeline.details.quarterly = Quarterly;
                state.commentingBeeline.status = 'fulfilled'
                state.commentingBeeline.isDataReceived = true
            })
            .addCase(fetchCommentingBeelineReport.rejected, (state, action) => {
                state.commentingBeeline.status = 'rejected'
            })
            // fetch Emails Sent Report
            .addCase(fetchEmailsSentReport.pending, (state, action) => {
                state.emailsSent.status = 'pending'
                state.emailsSent.details = detailsDefaultData
            })
            .addCase(fetchEmailsSentReport.fulfilled, (state, action) => {
                const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
                state.emailsSent.overview = Overview || state.emailsSent.overview
                if(Daily) state.emailsSent.details.daily = Daily;
                if(Weekly) state.emailsSent.details.weekly = Weekly;
                if(Monthly) state.emailsSent.details.monthly = Monthly;
                if(Quarterly) state.emailsSent.details.quarterly = Quarterly;
                state.emailsSent.status = 'fulfilled'
                state.emailsSent.isDataReceived = true
            })
            .addCase(fetchEmailsSentReport.rejected, (state, action) => {
                state.emailsSent.status = 'rejected'
            })
            // fetch Avg In View Data Report
            .addCase(fetchAvgInViewReport.pending, (state, action) => {
              state.avgInView.status = 'pending'
              state.avgInView.details = detailsDefaultData
            })
            .addCase(fetchAvgInViewReport.fulfilled, (state, action) => {
                const { Daily, Weekly, Monthly, Quarterly } = action.payload
                state.avgInView.overview = action.payload.Overview || state.avgInView.overview
                if(Daily) state.avgInView.details.daily = Daily;
                if(Weekly) state.avgInView.details.weekly = Weekly;
                if(Monthly) state.avgInView.details.monthly = Monthly;
                if(Quarterly) state.avgInView.details.quarterly = Quarterly;
                state.avgInView.status = 'fulfilled'
                state.avgInView.isDataReceived = true
            })
            .addCase(fetchAvgInViewReport.rejected, (state, action) => {
                state.avgInView.status = 'rejected'
            })
            // fetch import list
            .addCase(fetchImportLists.pending, (state, action) => {
                state.importLists.status = 'pending'
                state.importLists.data = []
            })
            .addCase(fetchImportLists.fulfilled, (state, action) => {
                const data = action.payload
                state.importLists.status = 'fulfilled'
                if(Array.isArray(data)) 
                    state.importLists.data = data
                if(typeof data === 'object')
                    state.importLists.data = [{...action.payload}]
            })
            .addCase(fetchImportLists.rejected, (state, action) => {
                state.importLists.status = 'rejected'
                state.importLists.data = []
            })

            // fetch commenting request status
            .addCase(fetchCommentingRequestStatus.pending, (state, action) => {
              state.commentingAccessRequest = {...state.commentingAccessRequest, requestStatus: null, userEmail: null }
              state.commentingAccessRequest.isFetching = true
            })
            .addCase(fetchCommentingRequestStatus.fulfilled, (state, action) => {
              const data = action.payload
              state.commentingAccessRequest = {...state.commentingAccessRequest, ...data}
              state.commentingAccessRequest.isFetching = false
            })
            .addCase(fetchCommentingRequestStatus.rejected, (state, action) => {
              state.commentingAccessRequest = {...state.commentingAccessRequest, requestStatus: null, userEmail: null }
              state.commentingAccessRequest.isFetching = false
            })

            // approve commenting request status
            .addCase(approveCommentingAccessRequest.pending, (state, action) => {
              state.commentingAccessRequest.isApproving = true
            })
            .addCase(approveCommentingAccessRequest.fulfilled, (state, action) => {
              const data = action.payload
              state.commentingAccessRequest = {...state.commentingAccessRequest, ...data }
              state.commentingAccessRequest.isApproving = false
            })
            .addCase(approveCommentingAccessRequest.rejected, (state, action) => {
              state.commentingAccessRequest = {...state.commentingAccessRequest, requestStatus: null, userEmail: null }
              state.commentingAccessRequest.isApproving = false
            })

            // fetch commenting config
            .addCase(fetchCommentingConfig.pending, (state, action) => {
                state.commentingConfig.status = 'pending'
                state.commentingConfig.data = null
            })
            .addCase(fetchCommentingConfig.fulfilled, (state, action) => {
                state.commentingConfig.status = 'fulfilled'
                state.commentingConfig.data = action.payload
            })
            .addCase(fetchCommentingConfig.rejected, (state, action) => {
                state.commentingConfig.status = 'rejected'
                state.commentingConfig.data = null
            })

            // fetch commenting unit code
            .addCase(fetchCommentingUnitCode.pending, (state, action) => {
                state.commentingUnitCode = {status: 'pending', bodyCode: '', headerCode: ''}
            })
            .addCase(fetchCommentingUnitCode.fulfilled, (state, action) => {
                state.commentingUnitCode = {status: 'fulfilled', ...action.payload}
            })
            .addCase(fetchCommentingUnitCode.rejected, (state, action) => {
                state.commentingUnitCode = {status: 'rejected', bodyCode: '', headerCode: ''}
            })

            // get updated codes
            .addCase(getUpdatedCodes.pending, (state, action) => {
                state.commentingAdvanced.codes = initialCommentingUnitCodes
            })
            .addCase(getUpdatedCodes.fulfilled, (state, action) => {
                state.commentingAdvanced.codes = action.payload
            })
            .addCase(getUpdatedCodes.rejected, (state, action) => {
                state.commentingAdvanced.codes = initialCommentingUnitCodes
            })

            // get migration status
            .addCase(getMigrationStatus.pending, (state, action) => {
                state.commentingAdvanced.isFetching = true
                state.commentingAdvanced.migrationStatus = null
            })
            .addCase(getMigrationStatus.fulfilled, (state, action) => {
                state.commentingAdvanced.isFetching = false
                state.commentingAdvanced.migrationStatus = action.payload
            })
            .addCase(getMigrationStatus.rejected, (state, action) => {
                state.commentingAdvanced.isFetching = false
                state.commentingAdvanced.migrationStatus = null
            })

            // get add ons 
            .addCase(getAddOns.pending, (state, action) => {
              state.commentingAddOns.isFetching = true
              state.commentingAddOns.addOns = initialCommentingAddOns
            })
            .addCase(getAddOns.fulfilled, (state, action) => {
                const { commentsCounter, community } = action.payload
                state.commentingAddOns.isFetching = false 
                state.commentingAddOns.addOns = {
                  commentsCounter: commentsCounter || initialCommentingAddOns.commentsCounter,
                  community: community || initialCommentingAddOns.community
                }
            })
            .addCase(getAddOns.rejected, (state, action) => {
                state.commentingAddOns.isFetching = false 
                state.commentingAddOns.addOns = initialCommentingAddOns
            })

            // get comments counter body code
            .addCase(getCommentsCounterCode.fulfilled, (state, action) => {
                const { commentsCounter } = action.payload
                if(commentsCounter) state.commentingAddOns.addOns.commentsCounter.bodyCode = commentsCounter.bodyCode
            })

            // create add ons
            .addCase(createAddOnsSetting.pending, (state, action) => {
                state.commentingAddOns.isCreating = true
            })
            .addCase(createAddOnsSetting.fulfilled, (state, action) => {
              if(action.payload) {
                const { type, data } = action.payload
                state.commentingAddOns.addOns[type] = data
              }
              state.commentingAddOns.isCreating = false
            })
            .addCase(createAddOnsSetting.rejected, (state, action) => {
                state.commentingAddOns.isCreating = false
            })

            // update add ons
            .addCase(updateAddOnsSetting.pending, (state, action) => {
                state.commentingAddOns.isUpdating = true
            })
            .addCase(updateAddOnsSetting.fulfilled, (state, action) => {
               if(action.payload) {
                const { type, data } = action.payload
                state.commentingAddOns.addOns[type] = data
               }
               state.commentingAddOns.isUpdating = false
            })
            .addCase(updateAddOnsSetting.rejected, (state, action) => {
                state.commentingAddOns.isUpdating = false
            })
            // update comments tree
            .addCase(updateCommentsTree.pending, (state, action) => {
              state.commentingConfig.isUpdating.commentsTree = true
            })
            .addCase(updateCommentsTree.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.commentsTree = false
              state.commentingConfig.data.commentsTree = data
            })
            .addCase(updateCommentsTree.rejected, (state, action) => {
              state.commentingConfig.isUpdating.commentsTree = false
            })      
             // update avatar pack
            .addCase(updateAvatarPack.pending, (state, action) => {
              state.commentingConfig.isUpdating.avatarPack = true
            })
            .addCase(updateAvatarPack.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.avatarPack = false
              state.commentingConfig.data.avatarPack = data
            })
            .addCase(updateAvatarPack.rejected, (state, action) => {
              state.commentingConfig.isUpdating.avatarPack = false
            })     
             // update color schema
             .addCase(updateColorSchema.pending, (state, action) => {
              state.commentingConfig.isUpdating.colorSchema = true
            })
            .addCase(updateColorSchema.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.colorSchema = false
              state.commentingConfig.data.colorSchema = data
            })
            .addCase(updateColorSchema.rejected, (state, action) => {
              state.commentingConfig.isUpdating.colorSchema = false
            })     
            // update community
            .addCase(updateCommunity.pending, (state, action) => {
              state.commentingConfig.isUpdating.community = true
            })
            .addCase(updateCommunity.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.community = false
              state.commentingConfig.data.community = data
            })
            .addCase(updateCommunity.rejected, (state, action) => {
              state.commentingConfig.isUpdating.community = false
            })    
            // update moderation
            .addCase(updateModeration.pending, (state, action) => {
              state.commentingConfig.isUpdating.moderation = true
            })
            .addCase(updateModeration.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.moderation = false
              state.commentingConfig.data.moderation = data
            })
            .addCase(updateModeration.rejected, (state, action) => {
              state.commentingConfig.isUpdating.moderation = false
            })          
            // update general
            .addCase(updateGeneral.pending, (state, action) => {
              state.commentingConfig.isUpdating.general = true
            })
            .addCase(updateGeneral.fulfilled, (state, action) => {
              const { data } = action.payload
              state.commentingConfig.isUpdating.general = false
              state.commentingConfig.data.general = data
            })
            .addCase(updateGeneral.rejected, (state, action) => {
              state.commentingConfig.isUpdating.general = false
            })            
    }
});

export default commentingSlice.reducer;
